import React, { useState } from "react";
import { EditTextPill } from "../HeaderCommon.js";
import ClippingValueInput from "./ClippingValueInput.js";
import Inventum from "../../../../../../engine/Inventum.js";

const CLIPPING_DISTANCE_RANGE = { min: -10000, max: 10000 };

function ClippingCollection(props) {
  const createPlane = () => {
    Inventum.clipping.createPlane();
    props.getClippingPlanes();
  };

  return (
    <div className="LabelDrawCollection">
      <div className="ClippingCreateButtonGroup">
        <button className="ClippingCreateButton" onClick={createPlane}>
          <i className="material-icons notranslate">add</i>
          <span>Create Clipping Plane</span>
        </button>
      </div>
      {props.clippingPlanes.map((plane, index) => (
        <PlaneEntry models={props.models} getClippingPlanes={props.getClippingPlanes} plane={plane} key={index} />
      ))}
    </div>
  );
}

function PlaneHeader(props) {
  const deletePlane = (e) => {
    e.stopPropagation();
    Inventum.clipping.deletePlane(props.plane.id);
    props.getClippingPlanes();
  };

  const duplicatePlane = (e) => {
    e.stopPropagation();
    Inventum.clipping.clonePlane(props.plane.id);
    props.getClippingPlanes();
  };

  const toggleActive = (e) => {
    e.stopPropagation();
    if (props.plane.active) {
      Inventum.clipping.deactivate(props.plane.id);
    } else {
      Inventum.clipping.activate(props.plane.id);
      Inventum.clipping.showHelper(props.plane.id);
    }
    props.getClippingPlanes();
  };

  const changeName = (text) => {
    Inventum.clipping.setName(props.plane.id, text);
    props.getClippingPlanes();
  };

  return (
    <div className="LabelHeader" onClick={props.toggleOpen}>
      <span className="PanelOpenStatus">{props.isOpen ? "▾" : "▸"}</span>
      <span className="LabelHeaderBtn delete" title="Delete Clipping Plane" onClick={deletePlane}>
        <i className="material-icons notranslate">clear</i>
      </span>
      <span className="LabelHeaderBtn" title="Clone Clipping Plane" onClick={duplicatePlane}>
        <i className="material-icons notranslate">copy_all</i>
      </span>
      <span className={"LabelHeaderBtn" + (props.plane.active ? " active" : "")} title="Activate Plane" onClick={toggleActive}>
        <i className="material-icons notranslate">{props.plane.active ? "check_box" : "check_box_outline_blank"}</i>
      </span>
      <span
        className={"LabelHeaderBtn" + (props.plane.previewingStart ? " active" : "")}
        title="Preview start position"
        onClick={(e) => {
          e.stopPropagation();
          Inventum.clipping.togglePreviewPosition(props.plane.id, "START");
          props.getClippingPlanes?.();
        }}>
        <i className="material-icons notranslate">skip_previous</i>
      </span>
      <span
        className={"LabelHeaderBtn" + (props.plane.previewingEnd ? " active" : "")}
        title="Preview end position"
        onClick={(e) => {
          e.stopPropagation();
          Inventum.clipping.togglePreviewPosition(props.plane.id, "END");
          props.getClippingPlanes?.();
        }}>
        <i className="material-icons notranslate">skip_next</i>
      </span>
      <EditTextPill
        setText={(text) => {
          changeName(text);
        }}
        value={props.plane.name}
        label={props.plane.name}
      />
    </div>
  );
}

function PlaneDrawer(props) {
  const startPos = props.plane.startPosition;
  const endPos = props.plane.endPosition;
  return (
    <div className="ClippingDrawer">
      <div className="ClippingDrawerSection">
        <h4 className="ClippingDrawerSectionTitle">Start Position</h4>
        <PositionEditor
          phi={startPos.phi}
          theta={startPos.theta}
          distance={startPos.distance}
          mode="START"
          planeID={props.plane.id}
          getClippingPlanes={props.getClippingPlanes}
          isPreviewActive={props.plane.previewingStart}
        />
      </div>
      <div className="ClippingDrawerSection">
        <h4 className="ClippingDrawerSectionTitle">
          End Position
          <span className="ClippingDrawerHint" title="Requires duration greater than 0 to use">*</span>
        </h4>
        <PositionEditor
          phi={endPos.phi}
          theta={endPos.theta}
          distance={endPos.distance}
          mode="END"
          planeID={props.plane.id}
          getClippingPlanes={props.getClippingPlanes}
          isPreviewActive={props.plane.previewingEnd}
        />
      </div>
      <div className="ClippingDrawerSection">
        <h4 className="ClippingDrawerSectionTitle">Animation</h4>
        <DurationInput
          name="Duration"
          value={props.plane.duration}
          min={0}
          max={60000}
          step={100}
          handleUpdate={(value) => {
            Inventum.clipping.setDuration(props.plane.id, Number(value));
          }}
        />
      </div>
      <div className="ClippingDrawerSection">
        <ModelSelector getClippingPlanes={props.getClippingPlanes} plane={props.plane} models={props.models} />
      </div>
    </div>
  );
}

function PositionEditor(props) {
  const { phi, theta, distance, planeID, mode, getClippingPlanes, isPreviewActive } = props;

  const activatePreview = () => {
    if (!isPreviewActive) {
      Inventum.clipping.togglePreviewPosition(planeID, mode);
      getClippingPlanes?.();
    }
  };

  const updateInventum = (key, value) => {
    const data = { phi, theta, distance, [key]: value };
    Inventum.clipping.editPosition(planeID, mode, data);
  };

  return (
    <div className="ClippingPositionEditor">
      <ClippingValueInput
        title="Dip"
        tooltip="Dip in degrees"
        value={phi}
        min={-360}
        max={360}
        onFocus={activatePreview}
        handleChange={(value) => updateInventum("phi", value)}
      />
      <ClippingValueInput
        title="Strike"
        tooltip="Strike in degrees"
        value={theta}
        min={-360}
        max={360}
        onFocus={activatePreview}
        handleChange={(value) => updateInventum("theta", value)}
      />
      <ClippingValueInput
        title="Distance"
        tooltip="Distance in units"
        value={distance}
        min={CLIPPING_DISTANCE_RANGE.min}
        max={CLIPPING_DISTANCE_RANGE.max}
        onFocus={activatePreview}
        handleChange={(value) => updateInventum("distance", value)}
      />
    </div>
  );
}

function ModelSelector(props) {
  const handleClick = (modelID) => {
    if (props.plane.models.includes(modelID)) {
      Inventum.clipping.removeModel(props.plane.id, modelID);
    } else {
      Inventum.clipping.addModel(props.plane.id, modelID);
    }
    props.getClippingPlanes();
  };

  return (
    <div className="ClippingModelSelector">
      <div className="ClippingModelSelectorHeader">
        <h4 className="ClippingDrawerSectionTitle">Affected Models</h4>
        <div className="ClippingModelActions">
          <button
            className="ClippingModelActionButton"
            onClick={() => {
              Inventum.clipping.copyModels(props.plane.id);
            }}>
            <i className="material-icons notranslate">content_copy</i>
            Copy
          </button>
          <button
            className="ClippingModelActionButton"
            onClick={() => {
              Inventum.clipping.pasteModels(props.plane.id);
            }}>
            <i className="material-icons notranslate">content_paste</i>
            Paste
          </button>
        </div>
      </div>
      <div className="ClippingModelList">
        {props.models.map((modelGroup, groupIndex) => {
          return (
            <div key={groupIndex} className="ClippingModelGroup">
              <div className="ClippingModelGroupName">{modelGroup.name}</div>
              <div className="ClippingModelGroupItems">
                {modelGroup.models.map((model, modelIndex) => {
                  return (
                    <button
                      className={"ClippingModelPill" + (props.plane.models.includes(model.id) ? " active" : "")}
                      key={modelIndex}
                      onClick={() => {
                        handleClick(model.id);
                      }}>
                      {model.name}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlaneEntry(props) {
  const [isOpen, setOpen] = useState(false);
  return (
    <div>
      <PlaneHeader
        plane={props.plane}
        isOpen={isOpen}
        getClippingPlanes={props.getClippingPlanes}
        toggleOpen={() => {
          props.getClippingPlanes();
          setOpen(!isOpen);
          if (isOpen) {
            Inventum.clipping.removeHelper(props.plane.id);
          }
        }}
      />
      {isOpen ? <PlaneDrawer models={props.models} plane={props.plane} getClippingPlanes={props.getClippingPlanes} /> : null}
    </div>
  );
}

class InventumClippingEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clippingPlanes: [],
      models: [],
    };
    this.getClippingPlanes = this.getClippingPlanes.bind(this);
  }

  componentDidMount() {
    this.getClippingPlanes();
    Inventum.clipping.setUIUpdateCallback(this.getClippingPlanes);
  }

  componentWillUnmount() {
    this.state.clippingPlanes.map(({ id }) => {
      Inventum.clipping.removeHelper(id);
    });
    Inventum.clipping.setUIUpdateCallback(null);
  }

  getClippingPlanes() {
    const clippingPlanes = Inventum.clipping.getClippingPlanes();
    const models = Inventum.models.getModelsSerialized();
    this.setState({ clippingPlanes: clippingPlanes, models: models });
  }

  render() {
    let style = {
      background: "#FFF",
      pointerEvents: "all",
    };

    const handleHelp = (e) => {
      e.stopPropagation();
      window.open("https://docs.inventum3d.com/clipping/", "_blank");
    };

    return (
      <div className="LabelsPanel">
        <div className="EditorTabHeader">
          <div className="EditorTabTitle">
            <i className="material-icons notranslate">compare</i>
            <span>Clipping Planes</span>
          </div>
          <button className="EditorTabHelpButton" title="Opens the Inventum Docs" onClick={handleHelp}>
            <i className="material-icons notranslate">help</i>
          </button>
        </div>
        <ClippingCollection
          models={this.state.models}
          getClippingPlanes={this.getClippingPlanes}
          clippingPlanes={this.state.clippingPlanes}
        />
      </div>
    );
  }
}

function DurationInput(props) {
  const { value, min, max, step, handleUpdate } = props;

  return (
    <div className="ClippingDurationInput">
      <ClippingValueInput
        title="Duration (ms)"
        tooltip="Duration in milliseconds"
        value={value}
        min={min}
        max={max}
        step={step}
        handleChange={(newValue) => handleUpdate(newValue)}
      />
    </div>
  );
}

export default InventumClippingEditor;
